import { type ReactNode, useState } from "react";

import { useOrganisationLogo } from "@/api/queries";
import { initials } from "@/lib/format";
import { useObjectUrl } from "@/lib/useObjectUrl";

import s from "./ui.module.css";

/**
 * An organisation's own logo on a white plate (logos are drawn for a light page, so the
 * plate keeps them readable in dark mode too), at most `height` pixels tall and keeping its
 * shape. Nothing is shown while it loads or when there is none. `src` shows a chosen file
 * instead, as a preview before it is saved. The alt names the organisation, unless `name`
 * is beside it already (`decorative`).
 */
export function OrganisationLogo({ name, version, src, height = 32, decorative = false }: { name: string; version?: string; src?: string; height?: number; decorative?: boolean }) {
  const logo = useOrganisationLogo(src ? undefined : version);
  const fetched = useObjectUrl(logo.data);
  const url = src ?? fetched;
  if (!url) return null;
  return (
    <span className={s.orgLogo} style={{ height: height + 8 }} data-logo={version}>
      <img src={url} alt={decorative ? "" : `${name} logo`} style={{ maxHeight: height }} />
    </span>
  );
}

/**
 * Whose console this is: the organisation's mark beside its name and a second line, in one card. The
 * mark is the logo on its white plate, or the organisation's initials while the logo loads and when
 * there is none, or `icon` in place of both. A wide logo (a wordmark, more than 1.6 times as wide as
 * tall) takes a row of its own above the name, so neither is squeezed. The mark is decorative: the
 * name is beside it. A long name wraps to two lines and is complete in the title.
 */
export function OrganisationBadge({ name, sub, logoVersion, icon }: { name: string; sub?: string; logoVersion?: string; icon?: ReactNode }) {
  const logo = useOrganisationLogo(icon ? undefined : logoVersion);
  const url = useObjectUrl(logo.data);
  const [wide, setWide] = useState(false);
  let mark: ReactNode;
  if (icon) mark = <span className={`${s.orgMark} ${s.orgMarkSoft}`} aria-hidden>{icon}</span>;
  else if (url) mark = (
    <span className={s.orgMark} data-logo={logoVersion}>
      <img src={url} alt="" onLoad={(event) => setWide(event.currentTarget.naturalWidth > 1.6 * event.currentTarget.naturalHeight)} />
    </span>
  );
  else mark = <span className={`${s.orgMark} ${s.orgMarkSoft}`} aria-hidden>{initials(name)}</span>;
  return (
    <div className={wide && url && !icon ? `${s.orgBadge} ${s.orgBadgeWide}` : s.orgBadge} title={name}>
      {mark}
      <span className={s.orgBadgeText}>
        <span className={s.orgBadgeName}>{name}</span>
        {sub ? <span className={s.orgBadgeSub}>{sub}</span> : null}
      </span>
    </div>
  );
}
